/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <Hooks dependencies are intentionally managed manually in this component due to complex state and store interactions that cause false positives.> */
import { Checkbox, Collapse, Divider, Icon, Tooltip } from '@mui/material'
import { getRoute } from 'helpers/route.helper/route.helper'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import filterStore, { NO_GROUPING, resetGrouping, setGrouping } from 'store/filter/filter'
import NewButton from 'ui/atoms/NewButton/NewButton'
import NewIcon from 'ui/atoms/NewIcon/NewIcon'
import SearchField from 'ui/atoms/SearchField/SearchField'
import { useSnapshot } from 'valtio'
import { getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'

type FilterDropdownProps = {
  label: string
  options: string[]
  translations?: { [key: string]: string }
  hiddenFilters?: string[]
  availableFilters: Set<string>
  onApply: (topLevelTag: string, value: Record<string, boolean>) => void
  initialCheckedFilters: Record<string, boolean>
}

const FilterDropdown = React.memo(
  ({
    label,
    options,
    translations,
    hiddenFilters = [],
    availableFilters,
    onApply,
    initialCheckedFilters,
  }: FilterDropdownProps) => {
    const BUTTONS_HEIGHT_FALLBACK = 50 // Fallback height for the buttons container if ref is not available
    const { t, i18n } = useTranslation()
    const route = getRoute()
    const { groupingPerRoute } = useSnapshot(filterStore)
    const dropdownKey = label
    const navigate = useNavigate()
    const dropdownLabel = getPrettyName(label, translations)
    const selectedLanguage = i18n.language
    const groupingPerRouteValue: string = groupingPerRoute[route] || NO_GROUPING
    const dropdownRef = useRef<HTMLDivElement>(null)
    const buttonsRef = useRef<HTMLDivElement>(null)
    const buttonsHeight = buttonsRef.current?.getBoundingClientRect().height || BUTTONS_HEIGHT_FALLBACK
    const scrollableContentRef = useRef<HTMLDivElement>(null)
    const [checkedFilters, setCheckedFilters] = useState<Record<string, boolean>>({ ...initialCheckedFilters })

    useEffect(() => {
      setCheckedFilters({ ...initialCheckedFilters })
    }, [initialCheckedFilters])

    const [searchValue, setSearchValue] = useState<string>('')
    const [selectAllIsChecked, setSelectAllIsChecked] = useState<boolean>(false)
    const [selectAllSearchedIsChecked, setSelectSearchedAllIsChecked] = useState<boolean>(false)
    const [expanded, setExpanded] = useState<boolean>(false)
    const [groupingToBeApplied, setGroupingToBeApplied] = useState<string>(groupingPerRouteValue)
    const [prevFilterChecked, setPrevFilterChecked] = useState<Record<string, boolean>>({})
    const [isContentOverflowing, setIsContentOverflowing] = useState<boolean>(false)

    const translatedChildren = useMemo(() => {
      return new Map(options.map((child) => [child, getPrettyName(child, translations)]))
    }, [options, translations])

    const filteredChildren = useMemo(() => {
      return options
        .filter((child) => availableFilters.has(`${label}/${child}`))
        .filter((child) => {
          const translatedChild = translatedChildren.get(child) ?? child
          const hiddenTags = hiddenFilters ?? []
          return (
            translatedChild.toLowerCase().includes(searchValue.toLowerCase()) &&
            !hiddenTags.includes(`${label}/${child}`)
          )
        })
    }, [searchValue, availableFilters, hiddenFilters, label, options, translatedChildren])

    const numberOfFiltersApplied = useMemo(() => {
      return Object.values(checkedFilters).filter((value) => value === true).length
    }, [checkedFilters])

    const visibleKeys = filteredChildren.map((child) => `${label}/${child}`)
    const allChecked = visibleKeys.length > 0 && visibleKeys.every((key) => checkedFilters[key])

    const getTranslation = useCallback(
      (child: string): string => translatedChildren.get(child) ?? child,
      [translatedChildren]
    )

    function applyGrouping() {
      if (groupingToBeApplied === label) {
        setGrouping(label, navigate)
      } else if (groupingPerRouteValue === label && groupingToBeApplied !== label) {
        resetGrouping(navigate)
      }
    }

    function noChangesToFilter(): boolean {
      // Check if prevFilterChecked contains the same elements as checkedFilters (does not check for grouping checkbox)
      const isSame =
        Object.entries(checkedFilters).length === Object.entries(prevFilterChecked).length &&
        Object.entries(checkedFilters).every(([key, value]) => prevFilterChecked[key] === value)
      return isSame
    }

    const handleOnApply = useCallback(() => {
      applyGrouping()
      const noChanges = noChangesToFilter()

      if (noChanges) {
        //make sure that we don't apply the filters if there are no changes
        setExpanded(false)
        return
      }
      //now we know that there are changes to the filters
      const filtersToBeApplied: Record<string, boolean> = {}

      Object.keys(checkedFilters).forEach((key) => {
        filtersToBeApplied[key] = !!checkedFilters[key]
      })

      setSearchValue('')
      setExpanded(false)

      onApply(label, filtersToBeApplied)
    }, [checkedFilters, groupingToBeApplied, label, onApply, groupingPerRouteValue, prevFilterChecked])

    const handleOnResetDropdownFilters = useCallback(() => {
      const newCheckedFilters = { ...checkedFilters }
      Object.keys(newCheckedFilters).forEach((key) => {
        newCheckedFilters[key] = false
      })
      setCheckedFilters(newCheckedFilters)
    }, [dropdownKey])

    const handleOnSelectAll = useCallback(() => {
      const newCheckedFilters = { ...checkedFilters }
      filteredChildren.forEach((child) => {
        newCheckedFilters[`${label}/${child}`] = !selectAllIsChecked
        setCheckedFilters(newCheckedFilters)
      })
    }, [dropdownKey, selectAllIsChecked, filteredChildren, label, checkedFilters])

    // Set all checkboxes that displays after a search to true if Select All Searched is checked
    const searchedFilters = useMemo(
      () =>
        filteredChildren.filter((child) => {
          const searchValues = searchValue.toLowerCase()
          const filterLabel = getPrettyName(child, translations).toLowerCase()
          return filterLabel.includes(searchValues)
        }),
      [filteredChildren, searchValue, translations]
    )

    const handleOnSelectSearched = useCallback(() => {
      const newCheckedFilters = { ...checkedFilters }
      searchedFilters.forEach((child) => {
        newCheckedFilters[`${label}/${child}`] = selectAllSearchedIsChecked ? false : true
        setCheckedFilters(newCheckedFilters)
      })
    }, [dropdownKey, label, selectAllSearchedIsChecked, searchedFilters, checkedFilters])

    // Set all checkboxes to true if Select All is checked
    useEffect(() => {
      setSelectAllIsChecked(allChecked)
    }, [allChecked])

    useEffect(() => {
      const allChecked =
        filteredChildren.length > 0 && filteredChildren.every((child) => checkedFilters[`${label}/${child}`])
      setSelectSearchedAllIsChecked(allChecked)
    }, [filteredChildren, checkedFilters])

    useEffect(() => {
      setGroupingToBeApplied(groupingPerRouteValue)
    }, [groupingPerRouteValue])

    // Handles click outside of the dropdown which should close it and apply the filters
    useEffect(() => {
      if (!expanded) return

      function handleClickOutsideDropdown(event: MouseEvent) {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setExpanded(false)
          applyGrouping()

          const noChanges = noChangesToFilter()
          if (noChanges) {
            //make sure that we don't apply the filters if there are no changes
            return
          } else {
            handleOnApply()
          }
        }
      }

      document.addEventListener('mousedown', handleClickOutsideDropdown)
      return () => {
        document.removeEventListener('mousedown', handleClickOutsideDropdown)
      }
    }, [expanded, handleOnApply])

    // Check if scrollable content is overflowing. This is used to align group filter and select all checkboxes with the other checkboxes when they are scrollable
    useEffect(() => {
      if (expanded) {
        const element = scrollableContentRef.current
        if (element) {
          setIsContentOverflowing(element.scrollHeight > element.clientHeight)
        }
      }
    }, [expanded])

    const ShortenWithTooltip: React.FC<{ text: string }> = ({ text }) => {
      const spanRef = useRef<HTMLSpanElement>(null)
      const [isTruncated, setIsTruncated] = useState(false)

      useEffect(() => {
        const el = spanRef.current
        if (el) {
          setIsTruncated(el.scrollWidth > el.clientWidth)
        }
      }, [text])

      const span = (
        <span ref={spanRef} className="block w-full truncate">
          {text}
        </span>
      )

      return isTruncated ? (
        <Tooltip title={text} arrow placement="top">
          {span}
        </Tooltip>
      ) : (
        span
      )
    }

    return (
      <div
        ref={dropdownRef}
        className="relative self-center"
        data-testid={`filter-category-${label}`} // e2e: filter category dropdown keyed by category key — do not remove
      >
        <div
          className={`flex flex-row items-center justify-between border-gray-300 w-62 h-10 border ${expanded ? 'rounded-t-md border-b-0' : 'rounded-md'} p-2 cursor-pointer`}
          onClick={() => {
            setExpanded(!expanded)
            if (!expanded) {
              //if we open the dropdown, we want to save the current state
              setPrevFilterChecked({ ...checkedFilters })
            }
          }}
        >
          <div className="mx-1 flex flex-row justify-between w-full">
            {dropdownLabel}
            <div className="flex gap-1">
              {groupingPerRouteValue === label && (
                <Tooltip title={t('Group filter applied')} arrow placement="top">
                  <span className="border border-vercel-primary rounded-md px-1">
                    {/* @ts-ignore: It states extra-small doesn't exists even when it does*/}
                    <Icon className="fas fa-layer-group" fontSize="extra-small" color="primary" />
                  </span>
                </Tooltip>
              )}
              {numberOfFiltersApplied > 0 && (
                <Tooltip
                  title={t('{{numberOfFilters}} filters applied', { numberOfFilters: numberOfFiltersApplied })}
                  arrow
                  placement="top"
                >
                  <span
                    className={`border border-vercel-primary rounded-md ${numberOfFiltersApplied > 9 ? 'px-1' : 'px-1.5'} text-vercel-primary`}
                  >
                    {numberOfFiltersApplied}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
          <NewIcon icon={expanded ? 'fal fa-chevron-down' : 'fal fa-chevron-right'} color="black" ariaLabel="expand" />
        </div>
        <Collapse in={expanded} timeout="auto">
          <div className="flex flex-col w-62 bg-white border border-gray-300 rounded-b-md border-t-0 p-2 absolute z-10">
            <SearchField
              value={searchValue}
              onChange={(value, e) => {
                setSearchValue(`${value}`)
                e?.preventDefault()
                e?.stopPropagation()
              }}
              onClear={() => setSearchValue('')}
            />
            <div className="flex flex-col">
              {Object.keys(groupingPerRoute).some((key) => key.includes(route)) && (
                <div
                  className="flex flex-row items-center justify-between bg-gray-200 rounded-md pl-1.5 mt-2 hover:cursor-pointer hover:bg-gray-300"
                  style={{
                    scrollbarGutter: isContentOverflowing ? 'stable' : 'auto',
                    overflowY: isContentOverflowing ? 'hidden' : 'auto',
                  }}
                  onClick={() => {
                    setGroupingToBeApplied((prev) => (prev === label ? NO_GROUPING : label))
                  }}
                >
                  {t('Group filter')}
                  <Checkbox
                    checked={groupingToBeApplied === label}
                    onClick={(e) => {
                      e.stopPropagation()
                      setGroupingToBeApplied((prev) => (prev === label ? NO_GROUPING : label))
                    }}
                  />
                </div>
              )}
              {searchValue === '' ? (
                <div
                  className="flex flex-row items-center justify-between rounded-md pl-1.5 my-1 hover:bg-gray-100 hover:cursor-pointer"
                  style={{
                    scrollbarGutter: isContentOverflowing ? 'stable' : 'auto',
                    overflowY: isContentOverflowing ? 'hidden' : 'auto',
                  }}
                  onClick={() => {
                    handleOnSelectAll()
                    setSelectAllIsChecked((prevState) => !prevState)
                  }}
                >
                  {t('Select all')}
                  <Checkbox
                    checked={selectAllIsChecked}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleOnSelectAll()
                      setSelectAllIsChecked((prevState) => !prevState)
                    }}
                  />
                </div>
              ) : (
                <div
                  className="flex flex-row items-center justify-between rounded-md pl-1.5 my-1 hover:bg-gray-100 hover:cursor-pointer"
                  style={{
                    scrollbarGutter: isContentOverflowing ? 'stable' : 'auto',
                    overflowY: isContentOverflowing ? 'hidden' : 'auto',
                  }}
                  onClick={() => {
                    handleOnSelectSearched()
                    setSelectSearchedAllIsChecked((prevState) => !prevState)
                  }}
                >
                  {t('Select searched filters')}
                  <Checkbox
                    checked={selectAllSearchedIsChecked}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleOnSelectSearched()
                      setSelectSearchedAllIsChecked((prevState) => !prevState)
                    }}
                  />
                </div>
              )}

              <Divider />
              <div
                ref={scrollableContentRef}
                className="overflow-y-auto flex-grow"
                style={{
                  maxHeight: `calc(40vh - ${buttonsHeight}px)`,
                }}
              >
                {filteredChildren
                  .sort((a, b) => getTranslation(a).localeCompare(getTranslation(b), selectedLanguage))
                  .map((child, index) => (
                    <div
                      className="flex flex-row items-center justify-between rounded-md pl-1.5 my-1 hover:bg-gray-100 hover:cursor-pointer"
                      key={`${child}.${index}`}
                      data-testid={`filter-option-${label}-${child}`} // e2e: filter option row keyed by category key and option value — do not remove
                      onClick={() => {
                        const newCheckedFilters = { ...checkedFilters }
                        newCheckedFilters[`${label}/${child}`] = !checkedFilters[`${label}/${child}`]
                        setCheckedFilters(newCheckedFilters)
                      }}
                    >
                      <ShortenWithTooltip text={getPrettyName(child, translations)} />
                      <Checkbox
                        checked={checkedFilters[`${label}/${child}`] || false}
                        onClick={() => {
                          const newCheckedFilters = { ...checkedFilters }
                          newCheckedFilters[`${label}/${child}`] = !checkedFilters[`${label}/${child}`]
                          setCheckedFilters(newCheckedFilters)
                        }}
                      />
                    </div>
                  ))}
              </div>
            </div>

            <div
              ref={buttonsRef}
              className="flex flex-row items-center justify-between bg-white sticky bottom-0 pt-2 border-t border-gray-300"
            >
              <NewButton
                variant="outlined"
                label={t('Reset filters')}
                onClick={handleOnResetDropdownFilters}
                disabled={Object.values(checkedFilters).every((value) => value === false)}
                data-testid={`filter-reset-${label}`} // e2e: per-dropdown reset keyed by category key — do not remove
              />
              <NewButton
                label={t('Apply')}
                onClick={handleOnApply}
                data-testid={`filter-apply-${label}`} // e2e: per-dropdown apply keyed by category key — do not remove
              />
            </div>
          </div>
        </Collapse>
      </div>
    )
  }
)

export default FilterDropdown
